import { makeStyles } from '@material-ui/core';
import api from 'api';
import { BoxWrapper, Button, Loader, Modal } from 'components/Base';
import Fields from 'components/Fields';
import { Field, Formik, FormikProps } from 'formik';
import React, { useState } from 'react';
import { errHelper } from 'utils';
import FoundFunds from './FoundFunds';
import FoundMultipleFunds from './FoundMulitpleFunds';
import FundsNotFound from './FundsNotFound';
import { FundType } from 'store/modules/Funds/types';
import { FoundFundProp, ImportFundProps, MultiFoundFundProp } from './types';

const useStyles = makeStyles({
  foundRow: {
    padding: '1rem',
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  nameAndCheckbox: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  code: {
    width: '190px'
  },
  result: {
    width: '466px',
    textOverflow: 'hidden'
  },
  multipleCode: {
    width: '466px%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  select: {
    width: '20%'
  },
  resultColumn: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start'
  },
  title: {
    color: '#2f2f2f80',
    fontWeight: 500,
    padding: '1rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderTop: '1px solid rgba(224, 224, 224, 1)'
  },
  search: {
    marginBottom: '1rem'
  },
  bottomBar: {
    backgroundColor: 'white',
    width: '100%',
    borderBottomRightRadius: '25px',
    borderBottomLeftRadius: '25px',
    height: '69px',
    borderTop: '1px solid rgba(224, 224, 224, 1)',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '1rem',
    alignContent: 'center'
  }
});

const BulkImport = ({
  openBulkImport,
  setOpenBulkImport,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  importToPolicy = () => {}
}: any) => {
  const classes = useStyles();

  const [search, setSearch] = useState('');
  const [imports, setImports] = useState({
    found: [],
    multipleFound: [],
    notFound: []
  } as ImportFundProps);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);

  const hasResults =
    !!(imports.found.length !== 0) ||
    !!(imports.multipleFound.length !== 0) ||
    !!(imports.notFound.length !== 0);

  const hideSearchBar = !isLoading && hasResults;

  const bulkImport = (codesList: string[]) => {
    const codes = codesList.filter(code => code.length !== 0 && code !== '-');
    setIsLoading(true);
    api
      .bulkImportFunds(codes)
      .then(response => {
        const importedData = response.data.reduce(
          (acc: any, imported: any, i: number) => {
            if (typeof imported === 'string') {
              acc.notFound.push({
                code: codes[i],
                result: imported
              });
            }
            if (Array.isArray(imported)) {
              acc.multipleFound.push({
                code: codes[i],
                result: imported
              });
            }
            if (typeof imported === 'object' && !Array.isArray(imported)) {
              acc.found.push({
                code: codes[i],
                result: imported,
                isSelected: true
              });
            }
            return acc;
          },
          { found: [], multipleFound: [], notFound: [] }
        );
        setImports(importedData);
        setIsLoading(false);
      })
      .catch(err => {
        errHelper(err);
      });
  };

  const parseFund = (fund: FundType) => {
    return {
      fund,
      active: fund.active,
      fund_value: '£0.00',
      allocation: '0.00%',
      source_value: 1,
      existing_weighting: 0
    };
  };

  const importFunds = (imports: ImportFundProps) => {
    const selectedImports = [] as any;
    imports.found.forEach((found: FoundFundProp) => {
      if (found.isSelected) {
        selectedImports.push(parseFund(found.result));
      }
    });
    imports.multipleFound.forEach((mf: MultiFoundFundProp) => {
      mf.result.forEach((found: FundType) => {
        if (found.isSelected) selectedImports.push(parseFund(found));
      });
    });
    importToPolicy(selectedImports.reverse());
    setOpenBulkImport(false);
    setSearch('');
    setImports({
      found: [],
      multipleFound: [],
      notFound: []
    });
  };

  const closeModal = () => {
    setSearch('');
    setError(false);
    setImports({ found: [], multipleFound: [], notFound: [] });
    setOpenBulkImport(false);
  };

  return (
    <Modal
      isOpen={openBulkImport}
      closeModalFunc={() => {
        setError(false);
        setOpenBulkImport(false);
      }}
    >
      <Formik
        enableReinitialize
        initialValues={{
          search: search,
          imports: imports
        }}
        onSubmit={(values, { resetForm }) => {
          bulkImport(values.search.replace(/[\s|\n]/gi, ',').split(','));
          resetForm();
        }}
      >
        {({
          handleSubmit,
          values,
          resetForm,
          setFieldValue
        }: FormikProps<any>) => {
          return (
            <BoxWrapper
              title="Bulk Importer"
              closeModal={() => {
                closeModal();
                resetForm();
              }}
            >
              <form onSubmit={handleSubmit}>
                <div
                  style={{
                    width: '900px',
                    height: '550px',
                    padding: '1rem',
                    overflow: 'auto',
                    paddingBottom: '100px'
                  }}
                >
                  <div
                    className={classes.search}
                    style={{
                      visibility: hideSearchBar ? 'hidden' : 'visible',
                      height: hideSearchBar ? 0 : 'max-content'
                    }}
                  >
                    <Field
                      label="Search ISIN"
                      name="search"
                      component={Fields.v2.Text}
                      InputProps={{
                        onChange: (e: any) => {
                          setSearch(e.target.value);
                          handleSubmit(values);
                        }
                      }}
                      isErrorPresent={error}
                    />
                  </div>

                  <Loader isLoading={isLoading}>
                    <FoundFunds foundFunds={imports.found} classes={classes} />

                    <FoundMultipleFunds
                      mulitpleFoundFunds={imports.multipleFound}
                      classes={classes}
                      setFieldValue={setFieldValue}
                    />

                    <FundsNotFound
                      fundsNotFound={imports.notFound}
                      classes={classes}
                    />
                  </Loader>
                </div>
                <div className={classes.bottomBar}>
                  <Button
                    onClick={() => {
                      closeModal();
                      resetForm();
                    }}
                    color="primary"
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button
                    key="Investment policy summary button"
                    onClick={() => importFunds(values.imports)}
                    type="button"
                    disabled={!hasResults}
                  >
                    Import
                  </Button>
                </div>
              </form>
            </BoxWrapper>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default BulkImport;
